/**
 * Actions Library management for super_admin users.
 * Manages action type definitions - the building blocks used in module workflows.
 */

import {
	getActionIcon,
	registerFallbackIcon,
} from "@unios/action-plugins/frontend";
import { ExternalLink, FormInput, MessageSquare, Zap } from "lucide-react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";

// Register fallback icons for base action types
registerFallbackIcon("comment", MessageSquare);
registerFallbackIcon("input", FormInput);
registerFallbackIcon("automation", Zap);
registerFallbackIcon("link", ExternalLink);

type ActionType = "comment" | "input" | "automation" | "link";

interface ActionTypeRecord {
	id: string;
	slug: string;
	name: string;
	description: string | null;
	type: ActionType;
	completionType: string;
	configSchema: Record<string, unknown> | null;
	defaultConfig: Record<string, unknown> | null;
	enabled: boolean;
	order: number;
	createdAt: Date;
}

/**
 * Get the icon for an action type
 * @param slug - The unique identifier for the action type
 * @param type - The category of action (comment, input, automation, link)
 */
function getIconForActionType(slug: string, type: string): ComponentType<{ className?: string }> {
	const icon = getActionIcon(slug, type);
	return icon ?? MessageSquare;
}

/**
 * Get badge color variant for action type
 * @param type - The category of action (comment, input, automation, link)
 */
function getTypeBadgeVariant(type: ActionType): "default" | "secondary" | "destructive" | "outline" {
	switch (type) {
		case "comment":
			return "secondary";
		case "input":
			return "default";
		case "automation":
			return "destructive";
		case "link":
			return "outline";
	}
}

/**
 * Actions Library management tab
 * Lists and manages action type definitions.
 * Uses URL navigation to open the EditorDrawer for create/edit.
 */
export function ActionsLibraryTab() {
	const [, setSearchParams] = useSearchParams();
	const utils = trpc.useUtils();

	const actionTypesQuery = trpc.actions.listAllActionTypes.useQuery();

	const toggleEnabledMutation = trpc.actions.toggleActionTypeEnabled.useMutation({
		onSuccess: () => {
			void utils.actions.listAllActionTypes.invalidate();
			void utils.actions.listActionTypes.invalidate();
		},
	});

	/**
	 * Opens the drawer for creating a new action type
	 */
	const handleCreate = () => {
		setSearchParams({ drawer: "action" });
	};

	/**
	 * Opens the drawer for editing an action type
	 * @param actionType - The action type record to edit
	 */
	const handleEdit = (actionType: ActionTypeRecord) => {
		setSearchParams({ drawer: "action", id: actionType.id });
	};

	/**
	 * Toggles the enabled status of an action type
	 * @param actionType - The action type record to toggle
	 */
	const handleToggleEnabled = (actionType: ActionTypeRecord) => {
		toggleEnabledMutation.mutate({
			id: actionType.id,
			enabled: !actionType.enabled,
		});
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">Actions Library</h2>
				<Button onClick={handleCreate}>Create Action</Button>
			</div>

			<div className="rounded-lg border">
				{actionTypesQuery.isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading actions...</div>
				) : actionTypesQuery.error ? (
					<div className="p-8 text-center text-destructive">
						Error loading actions: {actionTypesQuery.error.message}
					</div>
				) : actionTypesQuery.data?.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">No actions found.</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Slug</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Completion</TableHead>
								<TableHead className="w-[80px]">Enabled</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{actionTypesQuery.data?.map((actionType) => {
								const Icon = getIconForActionType(actionType.slug, actionType.type);
								return (
									<TableRow
										key={actionType.id}
										className="cursor-pointer hover:bg-muted/50"
										onClick={() => { handleEdit(actionType as ActionTypeRecord); }}
									>
										<TableCell className="font-medium">
											<div className="flex items-center gap-2">
												<Icon className="h-4 w-4 text-muted-foreground" />
												{actionType.name}
											</div>
										</TableCell>
										<TableCell>
											<code className="text-sm bg-muted px-1.5 py-0.5 rounded">
												{actionType.slug}
											</code>
										</TableCell>
										<TableCell>
											<Badge variant={getTypeBadgeVariant(actionType.type as ActionType)}>
												{actionType.type}
											</Badge>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{actionType.completionType}
										</TableCell>
										<TableCell onClick={(e) => { e.stopPropagation(); }}>
											<Checkbox
												checked={actionType.enabled}
												onCheckedChange={() => { handleToggleEnabled(actionType as ActionTypeRecord); }}
												disabled={toggleEnabledMutation.isPending}
											/>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</div>
		</div>
	);
}
